import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AdminField } from "@/pages/admin/AdminUx";
import {
  SURFACE,
  TAB_META,
  VIEW_STORAGE_KEY,
  fieldLabel,
  markHelp,
  MARK_MEANING,
  displayEvidenceMark,
} from "@/pages/admin/seoAeoGeoUx";
import "./seoAeoGeoUx.css";

const SeoAdminViewContext = createContext({
  view: "simple",
  setView: () => {},
  hasProvider: false,
});

export function useSeoAdminView() {
  return useContext(SeoAdminViewContext);
}

export function SeoAdminViewProvider({ children }) {
  const [view, setViewState] = useState(() => {
    try {
      return localStorage.getItem(VIEW_STORAGE_KEY) === "dev" ? "dev" : "simple";
    } catch {
      return "simple";
    }
  });
  const setView = (next) => {
    setViewState(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };
  const value = useMemo(() => ({ view, setView, hasProvider: true }), [view]);
  return <SeoAdminViewContext.Provider value={value}>{children}</SeoAdminViewContext.Provider>;
}

export function AdminViewModeToggle() {
  const { view, setView } = useSeoAdminView();
  return (
    <div className="seo-ux-view" role="group" aria-label="보기 모드" data-testid="seo-view-mode">
      <button
        type="button"
        className={view === "simple" ? "is-on" : ""}
        onClick={() => setView("simple")}
      >
        일반 보기
      </button>
      <button
        type="button"
        className={view === "dev" ? "is-on" : ""}
        onClick={() => setView("dev")}
        data-testid="search-dev-view"
      >
        개발자 보기
      </button>
    </div>
  );
}

export function AdminSectionIntro({ tabId }) {
  const { view } = useSeoAdminView();
  const meta = TAB_META[tabId];
  if (!meta) return null;
  return (
    <div className="seo-ux-intro" data-testid={`seo-intro-${tabId}`}>
      <h4>{view === "dev" ? meta.dev : meta.simple}</h4>
      <p>{meta.intro}</p>
    </div>
  );
}

export function SurfaceBadges({ surfaces }) {
  const ids = Array.isArray(surfaces) ? surfaces : [];
  if (!ids.length) return null;
  return (
    <span className="seo-ux-badges">
      {ids.map((id) => (
        <span key={id} className={`seo-ux-badge seo-ux-badge--${id}`}>
          {SURFACE[id]?.label || id}
        </span>
      ))}
    </span>
  );
}

export function SeoLabeledField({ meta, fieldKey, view, children }) {
  if (!meta) {
    return <AdminField label={fieldKey}>{children}</AdminField>;
  }
  return (
    <AdminField
      label={(
        <span className="seo-ux-field-label">
          <span>{fieldLabel(meta, fieldKey, view)}</span>
          <SurfaceBadges surfaces={meta.surfaces} />
        </span>
      )}
      hint={meta.hint}
    >
      {children}
    </AdminField>
  );
}

export function AdminSaveStatus({ dirty, saved, unpublished }) {
  return (
    <div className="seo-ux-status" data-testid="seo-save-status">
      {dirty ? <span className="seo-ux-pill seo-ux-pill--dirty">변경됨</span> : null}
      {!dirty && saved ? <span className="seo-ux-pill seo-ux-pill--saved">저장 완료</span> : null}
      {unpublished ? <span className="seo-ux-pill seo-ux-pill--draft">검색에 아직 반영되지 않음</span> : null}
    </div>
  );
}

export function AdminStickySaveBar({ dirty, saved, unpublished, onSave, saving, saveLabel = "저장", extra }) {
  return (
    <div className="seo-ux-sticky" data-testid="seo-sticky-bar">
      <AdminSaveStatus dirty={dirty} saved={saved} unpublished={unpublished} />
      <div className="seo-ux-sticky__actions">
        {extra}
        {onSave ? (
          <button type="button" className="btn-primary" disabled={saving || !dirty} onClick={onSave}>
            {saving ? "저장 중…" : saveLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function SeoLivePreview({
  title,
  url,
  description,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  aiItems,
  testPrefix = "seo",
}) {
  const serpTitle = title || "제목을 입력하세요";
  const serpDesc = description || "설명을 입력하세요";
  const serpUrl = url || "https://dsitesolution.com/";
  const shareTitle = ogTitle || title || "제목";
  const shareDesc = ogDescription || description || "설명";
  const shareUrl = ogUrl || url || "https://dsitesolution.com/";
  const host = String(shareUrl).replace(/^https?:\/\//, "").split("/")[0] || "dsitesolution.com";
  const items = (aiItems || []).filter((row) => row && (row.value || row.label));

  return (
    <div className="seo-ux-preview" data-testid={`${testPrefix}-live-preview`}>
      <div className="seo-ux-preview__card seo-serp" data-testid={`${testPrefix}-search-preview`}>
        <div className="seo-serp__kicker">Google 검색 결과</div>
        <div className="seo-serp__url">{serpUrl}</div>
        <div className="seo-serp__title">{serpTitle}</div>
        <div className="seo-serp__desc">{serpDesc}</div>
      </div>
      <div className="seo-ux-preview__card seo-ux-naver" data-testid={`${testPrefix}-naver-preview`}>
        <div className="seo-serp__kicker">Naver 검색 결과</div>
        <div className="seo-ux-naver__title">{serpTitle}</div>
        <div className="seo-ux-naver__url">{serpUrl}</div>
        <div className="seo-ux-naver__desc">{serpDesc}</div>
      </div>
      <div className="seo-ux-preview__card seo-og" data-testid={`${testPrefix}-og-preview`}>
        <div className="seo-og__kicker">SNS 공유 카드</div>
        <div className="seo-og__card">
          {ogImage ? (
            <img src={ogImage} alt="" className="seo-og__img" />
          ) : (
            <div className="seo-og__img seo-og__img--empty">이미지 없음</div>
          )}
          <div className="seo-og__body">
            <div className="seo-og__host">{host}</div>
            <div className="seo-og__title">{shareTitle}</div>
            <div className="seo-og__desc">{shareDesc}</div>
          </div>
        </div>
      </div>
      <div className="seo-ux-preview__card seo-ux-ai" data-testid={`${testPrefix}-ai-preview`}>
        <div className="seo-serp__kicker">AI가 이해할 핵심 정보</div>
        {items.length ? (
          <dl>
            {items.map((row) => (
              <div key={row.label}>
                <dt>{row.label}</dt>
                <dd>{row.value || "아직 없음"}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="seo-ux-ai__empty">입력하면 AI가 참고할 요약이 여기에 나타납니다.</p>
        )}
      </div>
    </div>
  );
}

export function EvidenceMark({ mark, kind, quality, evalRow, view, note, compact }) {
  const shown = displayEvidenceMark(note?.mark || mark);
  const label = note?.label || "";
  const status = shown === "O" ? "충분" : shown === "△" ? "일부 부족" : "부족";
  const title = note?.title || (label ? `${shown} ${label} ${status}` : `${shown} ${MARK_MEANING[shown] || ""}`);
  const why = note?.reason || (kind === "authority" ? (evalRow?.reason || markHelp(kind, shown)) : markHelp(kind, shown));
  const fix = note?.fix || (kind === "authority" ? (evalRow?.fix || "") : "");
  const titles = (evalRow?.direct_public || []).map((row) => row.title).filter(Boolean);
  const hover = [title, why, shown !== "O" && fix ? `O가 되려면: ${fix}` : ""].filter(Boolean).join("\n");
  const needsWhy = shown !== "O";
  return (
    <details
      className={`seo-ux-mark seo-ux-mark--${shown === "△" ? "partial" : shown === "O" ? "ok" : "no"}${compact ? " seo-ux-mark--compact" : ""}`}
      title={hover}
      data-testid={`evidence-mark-${kind}`}
    >
      <summary>
        <strong>{shown}</strong>
        {compact ? null : <span>{shown === "O" ? (MARK_MEANING[shown] || "충분") : `${label || ""} ${status}`.trim()}</span>}
      </summary>
      <div className="seo-ux-mark__why">
        {needsWhy ? <p className="seo-ux-mark__title">{title}</p> : null}
        {why ? <p>{why}</p> : null}
        {needsWhy && fix ? <p>O가 되려면: {fix}</p> : null}
        {titles.length ? <p>연결된 전문성: {titles.join(", ")}</p> : null}
        {quality && kind !== "authority" && view === "dev" ? (
          <p>내용품질 {quality} {MARK_MEANING[quality] || ""}</p>
        ) : null}
        {view === "dev" && evalRow ? (
          <p className="seo-ux-mark__dev">
            cause={evalRow.cause} · facts={(evalRow.direct_public || []).map((row) => row.id).join(", ") || "-"}
            {(evalRow.unpublished || []).length ? ` · unpublished=${evalRow.unpublished.map((row) => row.id).join(",")}` : ""}
          </p>
        ) : null}
      </div>
    </details>
  );
}

export function EvidenceFlow({ query, marks, quality, linkedLabels, view, authorityEval, markExplain }) {
  const steps = [
    { key: "service", simple: "연결된 서비스", dev: "Service", qualityKey: "service" },
    { key: "guide", simple: "가이드", dev: "Guide", qualityKey: "guide" },
    { key: "portfolio", simple: "포트폴리오", dev: "Portfolio", qualityKey: "portfolio" },
    { key: "authority", simple: "전문성 근거", dev: "Authority", qualityKey: "authority" },
  ];
  return (
    <div className="seo-ux-flow" data-testid="evidence-flow">
      <div className="seo-ux-flow__q">
        <span>질문</span>
        <strong>{query}</strong>
      </div>
      <div className="seo-ux-flow__steps">
        {steps.map((step) => (
          <div key={step.key} className="seo-ux-flow__step">
            <div className="seo-ux-flow__name">{view === "dev" ? step.dev : step.simple}</div>
            <EvidenceMark
              mark={marks?.[step.key]}
              quality={quality?.[step.qualityKey]}
              kind={step.key}
              view={view}
              evalRow={step.key === "authority" ? authorityEval : null}
              note={markExplain?.[step.key]}
            />
            {linkedLabels?.[step.key] ? (
              <div className="seo-ux-flow__links">{linkedLabels[step.key]}</div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminConfirmDialog({ open, title, message, confirmLabel = "삭제", onConfirm, onCancel }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div className="seo-ux-confirm" data-testid="seo-confirm-dialog">
      <button type="button" className="seo-ux-confirm__backdrop" aria-label="닫기" onClick={onCancel} />
      <div className="seo-ux-confirm__panel" role="dialog" aria-modal="true" aria-label={title}>
        <h4>{title}</h4>
        <p>{message}</p>
        <div className="seo-ux-confirm__actions">
          <button type="button" className="admin-row__btn" onClick={onCancel}>취소</button>
          <button type="button" className="seo-ux-confirm__danger" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
